import { Card, CardHeader, CardBody } from '@repo/ui';
import { ErrorCard } from '@xtnd/web/shared/components';
import { numberUtil } from '@xtnd/web/shared/utils';
import { SummaryCardsSkeleton, SummaryCardsGrid, type SummaryCardProps } from '../../../components';
import { useArSummaryStats } from '../../../queries/arReportQuery';

export function ArExceptionSummary() {
  const { data, isLoading, error, refetch } = useArSummaryStats();

  if (isLoading) {
    return (
      <SummaryCardsSkeleton
        count={6}
        columns={{ mobile: 1, tablet: 2, desktop: 3 }}
        gap="md"
        useSkeletonComponent={false}
      />
    );
  }

  if (error) {
    return (
      <ErrorCard
        title="Error Loading AR Exception Summary"
        message={error instanceof Error ? error.message : 'An unexpected error occurred'}
        onRetry={() => refetch()}
      />
    );
  }

  if (!data) {
    return null;
  }

  const summaryCards: Omit<SummaryCardProps, 'className'>[] = [
    {
      title: 'Total Records',
      value: numberUtil.formatNumber(data.totalRecords),
      subtitle: `${numberUtil.formatNumber(data.totalClioInvoices || 0)} from Clio, ${numberUtil.formatNumber(data.totalQboInvoices || 0)} from QBO`,
      color: 'text-xtnd-blue dark:text-xtnd-blue-400',
      bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
      tooltip:
        'Total number of reconciliation records. Each record represents either a matched pair (Clio + QBO) or a single invoice (missing in one system). The Clio and QBO counts show how many invoices from each source are included in these records.',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      title: 'Match Success Rate',
      value: numberUtil.formatPercentage(data.matchSuccessRate, 1, false),
      subtitle: `${numberUtil.formatNumber(data.actualMatchedRecords)} of ${numberUtil.formatNumber(data.totalRecords)} matched`,
      color:
        data.matchSuccessRate >= 90
          ? 'text-green-600'
          : data.matchSuccessRate >= 70
            ? 'text-amber-600'
            : 'text-red-600',
      bgColor:
        data.matchSuccessRate >= 90
          ? 'bg-green-50 dark:bg-green-900/20'
          : data.matchSuccessRate >= 70
            ? 'bg-amber-50 dark:bg-amber-900/20'
            : 'bg-red-50 dark:bg-red-900/20',
      tooltip:
        'Percentage of invoices that were successfully matched between Clio and QuickBooks. Only records with both Clio and QBO data are considered matched.',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      title: 'Total Variance',
      value: numberUtil.formatCurrency(data.totalVarianceAmount),
      subtitle: 'Absolute variance sum',
      color: data.totalVarianceAmount === 0 ? 'text-green-600' : 'text-red-600',
      bgColor: data.totalVarianceAmount === 0 ? 'bg-green-50' : 'bg-red-50',
      tooltip:
        'Sum of absolute differences between Clio and QuickBooks invoice amounts. A variance occurs when the amounts do not match exactly between the two systems.',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      title: 'Missing in QBO',
      value: numberUtil.formatNumber(data.missingInQbo),
      subtitle: '',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      tooltip:
        'Invoices that exist in Clio but are missing in QuickBooks. These invoices need to be reviewed and either added to QuickBooks or removed from Clio.',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
    {
      title: 'Missing in Clio',
      value: numberUtil.formatNumber(data.missingInClio),
      subtitle: '',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      tooltip:
        'Invoices that exist in QuickBooks but are missing in Clio. These invoices need to be reviewed and either added to Clio or removed from QuickBooks.',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
    {
      title: 'Duplicates',
      value: numberUtil.formatNumber(data.duplicates),
      subtitle: '',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      tooltip:
        'Invoice records that have duplicate BillIDs. These duplicates need to be reviewed and resolved to ensure accurate reconciliation.',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Main Summary Cards */}
      <SummaryCardsGrid
        cards={summaryCards.map(card => ({
          ...card,
          className: '',
        }))}
        columns={{ mobile: 1, tablet: 2, desktop: 3 }}
        gap="md"
      />

      {data.duplicates > 0 && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
              Additional Issues
            </h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {data.duplicates > 0 && (
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-purple-50 text-purple-600">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-purple-600">
                      {numberUtil.formatNumber(data.duplicates)}
                    </p>
                    <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400">
                      Duplicate BillIDs
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
